import { useState, ChangeEvent, FormEvent } from "react";
import NavBar from "../components/navBar";
import Register from "../components/Auth/auth";

function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [username, setUsername] = useState("");
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    if (!email || !fname || !lname || !confirmPassword) {
      setFieldErrors({ general: "Please fill in all required fields." });
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords do not match." });
      setIsLoading(false);
      return;
    }

    setFieldErrors({});

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      name: fname,
      lname: lname,
      email: email,
      username: username,
      password: password,
      role: "user",
      phone: phone,
      country: country,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
      mode: "cors",
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/`,
        requestOptions
      );

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errors = {};
          errorData.errors.forEach((error) => {
            errors[error.path] = error.msg;
          });
          setFieldErrors(errors);
        } else {
          throw new Error(`Registration failed: ${errorData.message}`);
        }
        return;
      }

      const data = await response.json();
      console.log("Registration successful:", data);

      localStorage.setItem("token", data.token);
      // Role lives on data.user.role (data.role is undefined), which used to
      // store the literal string "undefined" and break admin routing.
      localStorage.setItem("userRole", data.user?.role ?? data.role);
      localStorage.setItem("userId", data.user.userId);
      localStorage.setItem("email", data.user.email); 

      setFieldErrors({});

      if (data.user?.role === "admin") {
        window.location.href = "/admin-dashboard";
      } else {
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Registration failed:", error.message);
      setFieldErrors({ general: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-3xl w-full p-8 shadow-lg rounded-lg">
            <h1 className="text-3xl font-bold text-center pb-6">
              Create an account
            </h1>
            {fieldErrors.general && (
              <div role="alert" className="mb-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {fieldErrors.general}
              </div>
            )}
            {fieldErrors.confirmPassword && (
              <p role="alert" className="mb-3 text-sm text-red-400">
                {fieldErrors.confirmPassword}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="fname"
                    className="block text-lg font-medium mb-1"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="fname"
                    value={fname}
                    onChange={(e) => setFname(e.target.value)}
                    placeholder="Enter your first name"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.name ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="lname"
                    className="block text-lg font-medium mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lname"
                    value={lname}
                    onChange={(e) => setLname(e.target.value)}
                    placeholder="Enter your last name"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.lname ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.lname && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.lname}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-lg font-medium mb-1"
                  >
                    Phone Number
                  </label>
                  <input
                    type="text"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.phone ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="country"
                    className="block text-lg font-medium mb-1"
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Enter your country"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.country ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.country && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.country}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-lg font-medium mb-1"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.email ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-lg font-medium mb-1"
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.username ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.username && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.username}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-lg font-medium mb-1"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className={`w-full py-2 px-4 border ${
                      fieldErrors.password ? "border-red-500" : "border-border"
                    } rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none`}
                  />
                  {fieldErrors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.password}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-lg font-medium mb-1"
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full py-2 px-4 border border-border rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2 px-4 bg-red-600 font-semibold rounded-md hover:bg-red-500 focus:ring-2 focus:ring-red-600 focus:outline-none disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
            <p className="mt-4 text-center ">
              Already have an account?{" "}
              <a
                href="/login"
                className="text-red-600 font-medium hover:underline"
              >
                Login
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default SignUp;
